import { useEffect, useRef, useState } from 'react';

/**
 * Subscribes to Binance's public WebSocket for live 24h ticker data across ALL symbols.
 * Returns a map of `symbolUpper -> { price, changePct, dir }` updated in near-realtime.
 * No API key required. Public endpoint: wss://stream.binance.com:9443/ws/!ticker@arr
 */
export default function useBinanceTickers() {
  const [tickers, setTickers] = useState({});
  const wsRef = useRef(null);
  const prevPricesRef = useRef({});

  useEffect(() => {
    let mounted = true;
    let reconnectTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          if (!mounted) return;
          try {
            const arr = JSON.parse(evt.data);
            if (!Array.isArray(arr)) return;
            setTickers((prev) => {
              const next = { ...prev };
              for (const t of arr) {
                // t.s is the pair symbol like "BTCUSDT"; we care about *USDT pairs
                const s = t.s;
                if (!s?.endsWith('USDT')) continue;
                const base = s.slice(0, -4);
                const price = parseFloat(t.c);
                const changePct = parseFloat(t.P);
                const prevPrice = prevPricesRef.current[base];
                const dir = prevPrice == null ? 'none' : price > prevPrice ? 'up' : price < prevPrice ? 'down' : 'flat';
                prevPricesRef.current[base] = price;
                next[base] = { price, changePct, dir, at: Date.now() };
              }
              return next;
            });
          } catch (_e) { /* noop */ }
        };
        ws.onclose = () => {
          if (mounted) reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch(_e) {} };
      } catch (_e) {
        if (mounted) reconnectTimer = setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      mounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch(_e) {}
    };
  }, []);

  return tickers;
}
